/*
	Header.tsx -- Application logic for the Header bar.
*/
import { type JSX, useContext, useEffect, useState } from 'react';

import { AlertsLimitsApi } from 'Api/AlertsLimits/AlertsLimitsApi';
import { ChangePasswordApi } from 'Api/Auth/ChangePasswordApi';
import { LogoutApi } from 'Api/Auth/LogoutApi';
import { ClientDetailsApi } from 'Api/Client/ClientDetailsApi';
import { NotificationsApi } from 'Api/Notifications/NotificationsApi';
import { IconButton } from 'Components/Buttons/IconButton';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { RBACComponent } from 'Components/Context/RBACComponent';
import { RBACContext, userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Text } from 'Components/Text/Text';
import { SUPPORT_EMAIL_ADDRESS } from 'Config/HPSEmailAddresses';
import { ICON_SECURITY } from 'Config/Icons';
import { NumberOfAlertsToDisplay } from 'Config/Parameters';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { saveFileFromApi } from 'Helpers/FileUtils';
import { CONTROL_AUTOMATION_ROLES } from 'Helpers/UserUtils';
import { WindowNavigator } from 'Helpers/WindowNavigator';
import { AlertRequest, AlertResponse } from 'Models/Alerts';
import { UserNotification } from 'Models/Notification';

import styles from './Header.module.css';
import { HeaderAlerts, HeaderAlertsProps } from './HeaderAlerts/HeaderAlerts';
import { HeaderNotificationProps, HeaderNotifications } from './HeaderNotifications/HeaderNotifications';
import { HeaderProfile, HeaderProfileProps } from './HeaderProfile/HeaderProfile';

export interface HeaderProps {
    authHandler: AuthHandler;
    clientDetailsApi: ClientDetailsApi;
    isNavOpen: boolean;
    logoutApi: LogoutApi;
    setNavOpen: (open: boolean) => void;
    windowNavigator: WindowNavigator;
}

export interface HeaderNetworkingApis {
    alertsLimitsApi: AlertsLimitsApi;
    changePasswordApi: ChangePasswordApi;
    notificationsApi: NotificationsApi;
    windowNavigator: WindowNavigator;
}

export const Header = (props: HeaderProps & HeaderNetworkingApis): JSX.Element => {
    const rbacContext = useContext(RBACContext);
    const cachedData = useCachedData();

    const [usersFullName, setUsersFullName] = useState<string>('');
    const [notifications, setNotifications] = useState<UserNotification[]>([]);
    const [changePasswordModalOpen, setChangePasswordModalOpen] = useState<boolean>(false);
    const [alerts, setAlerts] = useState<AlertResponse[]>([]);

    /**
     * Get Alerts when the component mounts.
     */
    useEffect(() => {
        const getAlerts = async (): Promise<void> => {
            try {
                const alertRequest: AlertRequest = { limit: NumberOfAlertsToDisplay };
                const alertResponse = await props.alertsLimitsApi.getAllAlerts(alertRequest);
                setAlerts(alertResponse.data);
            } catch (error) {
                handleRequestError(error);
            }
        };
        if (rbacContext && userHasAuthorizedRole(rbacContext.roles, CONTROL_AUTOMATION_ROLES)) {
            getAlerts();
        }
    }, [props.alertsLimitsApi, rbacContext]);

    /**
     * Get Notifications when the component mounts.
     */
    useEffect(() => {
        const getNotificationsSelf = async (): Promise<void> => {
            try {
                const response = await props.notificationsApi.getNotificationsSelf();
                setNotifications(response.data);
            } catch (error) {
                handleRequestError(error);
            }
        };
        getNotificationsSelf();
    }, [props.notificationsApi]);

    /**
     * Get the current user's full name.
     */
    useEffect(() => {
        const getUsersFullName = async (): Promise<void> => {
            try {
                const full_name = props.authHandler.getAuthenticatedUserFullName(cachedData.users);
                setUsersFullName(full_name);
            } catch (error) {
                handleRequestError(error);
            }
        };
        getUsersFullName();
    }, [props.authHandler, cachedData.users]);

    const handleRequestError = (error: Error): void => console.error('Error: ', error);

    /**
     * Open a new tab allowing the user to change their password.
     */

    const handleChangePasswordClick = (): void => {
        setChangePasswordModalOpen(true);
    };

    const handleChangePassword = async (): Promise<string> => {
        await props.changePasswordApi.changePassword();
        return 'Email sent. Please check your inbox for the password reset link.';
    };

    /**
     * Log out and redirect to the login page.
     */
    const handleLogout = (): void => {
        try {
            props.logoutApi.logOut();
        } catch (err) {
            console.error('Error: ', err);
        }
    };

    /**
     * Download the User Manual.
     */
    const handleUserManualClick = async (): Promise<void> => {
        try {
            const fileDownload = (await props.clientDetailsApi.getCustomerDocumentation('User Manual.pdf')).data;
            saveFileFromApi('binary', fileDownload);
        } catch (err) {
            console.error('Error: ', err);
        }
    };

    /**
     * Download the Release Notes.
     */
    const handleReleaseNotesClick = async (): Promise<void> => {
        try {
            const fileDownload = (await props.clientDetailsApi.getCustomerDocumentation('Release Notes.pdf')).data;
            saveFileFromApi('binary', fileDownload);
        } catch (err) {
            console.error('Error: ', err);
        }
    };

    /**
     * Open the user's mail client to send an email to High Peaks Solutions support.
     */
    const handleSupportClick = (): void => {
        props.windowNavigator.openExternalUrl(`mailto:${SUPPORT_EMAIL_ADDRESS}`, false);
    };

    // Call the child "Header" component, to render the HTML for the Header bar.
    const headerProfile: HeaderProfileProps = {
        username: usersFullName,
        handleChangePasswordClick: handleChangePasswordClick,
        handleLogout: handleLogout,
        handleReleaseNotesClick: handleReleaseNotesClick,
        handleSupportClick: handleSupportClick,
        handleUserManualClick: handleUserManualClick,
    };
    const headerNotifications: HeaderNotificationProps = {
        notifications: notifications,
    };
    const headerAlertsProps: HeaderAlertsProps = {
        alerts: alerts,
    };

    const closeMenuUrl = new URL('/open-menu.svg', import.meta.url).href;

    return (
        <>
            {changePasswordModalOpen && (
                <ConfirmationModal headerText="Change Password" operationType={ConfirmationModalOperationType.DESTRUCTIVE} areYouSureText="Are you sure you want to change your password?" buttonIcon={ICON_SECURITY} buttonLoadingText="Sending Email" buttonText="Change Password" performOperation={handleChangePassword} hideModal={() => setChangePasswordModalOpen(false)}>
                    <Text>A password reset link will be sent to your email address.</Text>
                </ConfirmationModal>
            )}
            <div className={styles.Header}>
                <div className={props.isNavOpen === true ? styles.menuOpen : styles.menuClosed}>
                    <IconButton aria-label="Open Menu Navigation" onClick={() => props.setNavOpen(true)} iconImage={<img src={closeMenuUrl} alt="open-menu-navigation" />} />
                </div>
                <div className={styles.HeaderRight}>
                    <RBACComponent roles={CONTROL_AUTOMATION_ROLES}>
                        <HeaderAlerts {...headerAlertsProps} />
                    </RBACComponent>
                    <HeaderNotifications {...headerNotifications} />
                    <HeaderProfile {...headerProfile} />
                </div>
            </div>
        </>
    );
};
